import React from 'react';
import { cn } from './cn';
import type { Density } from './types';

/**
 * Toolbar — แถบเหนือตาราง: ชื่อ/จำนวน · ค้นหา · ตัวกรอง · ColumnPicker · ความหนาแน่น · ปุ่มหลัก
 * แบ่งเป็น 3 ช่อง (start / center / end) เพื่อให้ทุกหน้าวางของที่เดียวกัน
 */
export interface ToolbarProps {
  title?: string;
  /** จำนวนรายการทั้งหมด — แสดงข้างชื่อ */
  count?: number;
  start?: React.ReactNode;
  center?: React.ReactNode;
  end?: React.ReactNode;
  className?: string;
}

export const Toolbar: React.FC<ToolbarProps> = ({ title, count, start, center, end, className }) => (
  <div
    role="toolbar"
    aria-label={title ? `เครื่องมือของ ${title}` : 'แถบเครื่องมือ'}
    className={cn(
      'flex min-h-[var(--toolbar-height)] flex-wrap items-center gap-[var(--space-3)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-2)] font-[family-name:var(--font-thai)]',
      className,
    )}
  >
    {title && (
      <h2 className="flex items-baseline gap-[var(--space-2)] text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
        {title}
        {count !== undefined && (
          <span className="bo-num text-[length:var(--text-table)] font-[var(--weight-regular)] text-[var(--ink-600)]">
            {count.toLocaleString('th-TH')}
          </span>
        )}
      </h2>
    )}
    {start}
    {center && <div className="flex flex-1 items-center gap-[var(--space-2)]">{center}</div>}
    {end && <div className="ml-auto flex items-center gap-[var(--space-2)]">{end}</div>}
  </div>
);

const DENSITY_LABEL: Record<Density, string> = { compact: 'แน่น', normal: 'ปกติ', comfortable: 'สบาย' };
const DENSITY_ICON: Record<Density, string> = { compact: 'density_small', normal: 'density_medium', comfortable: 'density_large' };

/** ตัวเลือกความหนาแน่นของแถว — ค่าที่เลือกถูกจำไว้โดย useTableDensity() ที่หน้าจอเรียกใช้ */
export const DensityToggle: React.FC<{ value: Density; onChange: (v: Density) => void }> = ({ value, onChange }) => (
  <div role="radiogroup" aria-label="ความสูงของแถว" className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
    {(['compact', 'normal', 'comfortable'] as Density[]).map((d) => (
      <button
        key={d}
        type="button"
        role="radio"
        aria-checked={value === d}
        onClick={() => onChange(d)}
        title={DENSITY_LABEL[d]}
        className={cn(
          'inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[family-name:var(--font-thai)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]',
          value === d ? 'bg-[var(--blue-700)] text-white' : 'text-[var(--ink-700)] hover:bg-[var(--blue-050)]',
        )}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>
          {DENSITY_ICON[d]}
        </span>
        <span className="sr-only">{DENSITY_LABEL[d]}</span>
      </button>
    ))}
  </div>
);
